const TIMING: KeyframeAnimationOptions = { duration: 200, easing: "ease-out" };

/**
 * Animates the narrow layout's buttons moving onto their own row below the
 * question, and back: CSS can't transition an item between flex lines. The
 * buttons slide from where they were drawn while the box's height follows,
 * so the content below glides rather than jumps. Returns a cleanup.
 */
export function animateButtonRowSwitch(row: HTMLElement): () => void {
	const question = row.querySelector<HTMLElement>(".question-container");
	const actions = row.querySelector<HTMLElement>(
		".gvtui-question-box__actions",
	);
	const box = row.closest<HTMLElement>(".gvtui-question-box");
	if (!question || !actions || !box) {
		return () => {};
	}

	// offsetTop/offsetHeight are layout positions, unaffected by the transform
	// and height animations below.
	const isOnOwnRow = () =>
		actions.offsetTop >= question.offsetTop + question.offsetHeight;
	const layoutOffset = () => actions.offsetTop - row.offsetTop;

	let wasOnOwnRow = isOnOwnRow();
	let rowHeight = row.offsetHeight;
	let actionsOffset = layoutOffset();
	let running: Animation[] = [];

	const observer = new ResizeObserver(() => {
		const onOwnRow = isOnOwnRow();
		const inFlight = running.some((a) => a.playState === "running");
		// A resize mid-animation (typing on through the switch) restarts it
		// from where things are drawn, so nothing jumps.
		const animate =
			(onOwnRow !== wasOnOwnRow || inFlight) &&
			rowHeight > 0 &&
			!window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		let fromOffset = actionsOffset;
		let fromHeight: number | undefined;
		if (animate && inFlight) {
			fromOffset =
				actions.getBoundingClientRect().top - row.getBoundingClientRect().top;
			fromHeight = box.getBoundingClientRect().height;
		}
		for (const animation of running) {
			animation.cancel();
		}
		running = [];

		const nextRowHeight = row.offsetHeight;
		const nextOffset = layoutOffset();
		if (animate) {
			const toHeight = box.offsetHeight;
			fromHeight ??= toHeight - (nextRowHeight - rowHeight);
			running = [
				actions.animate(
					[
						{ transform: `translateY(${fromOffset - nextOffset}px)` },
						{ transform: "none" },
					],
					TIMING,
				),
				box.animate(
					[{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
					TIMING,
				),
			];
		}

		wasOnOwnRow = onOwnRow;
		rowHeight = nextRowHeight;
		actionsOffset = nextOffset;
	});
	observer.observe(row);

	return () => {
		observer.disconnect();
		for (const animation of running) {
			animation.cancel();
		}
	};
}
